import _get from 'lodash/get';
import _isNumber from 'lodash/isNumber';

import INITIAL_STATE from './initialState';

/**
 * Store the MFA session built from the authenticate API response.
 *
 * @method setMFASession
 * @param  {Object}      state  The current state
 * @param  {Object}      action The dispatched action
 * @return {Object}             The updated state
 */
export const setMFASession = (state, action) => {
    const payload = _get(action, 'payload', {});

    return {
        ...state,
        allowedMethods: _get(payload, 'allowedMethods', []),
        error: null,
        isVerifying: false,
        loginContext: _get(payload, 'loginContext', {}),
        method: _get(payload, 'method', ''),
        mfaToken: _get(payload, 'mfaToken', ''),
        remainingAttempts: null,
        status: _get(payload, 'status', ''),
    };
};

/**
 * Clear the MFA session, i.e. reset the 'mfa' state.
 *
 * @method clearMFASession
 * @return {Object} The initial state
 */
export const clearMFASession = () => ({ ...INITIAL_STATE });

/**
 * Clear the error of the last verification attempt.
 *
 * NOTE: the `mfaToken` and the attempts left are kept, the challenge is still
 * the same one and its attempts are owned by the API.
 *
 * @method clearMFAError
 * @param  {Object}     state The current state
 * @return {Object}           The updated state
 */
export const clearMFAError = state => ({
    ...state,
    error: null,
});

/**
 * Flag the verification request as in flight.
 *
 * @method verifyMFACodeRequest
 * @param  {Object}             state The current state
 * @return {Object}                   The updated state
 */
export const verifyMFACodeRequest = state => ({
    ...state,
    error: null,
    isVerifying: true,
});

/**
 * Handle a failed verification attempt.
 *
 * NOTE: the raw error is kept as is, it carries the `errorCode` the screen
 * derives its error state from. The `remainingAttempts` of the response is
 * stored on its own, so the attempts left survive the error being cleared.
 *
 * @method verifyMFACodeError
 * @param  {Object}           state  The current state
 * @param  {Object}           action The dispatched action
 * @return {Object}                  The updated state
 */
export const verifyMFACodeError = (state, action) => {
    const error = _get(action, 'payload', null);
    const remainingAttempts = _get(error, 'response.data.remainingAttempts', null);

    return {
        ...state,
        error,
        isVerifying: false,
        remainingAttempts: _isNumber(remainingAttempts)
            ? remainingAttempts
            : _get(state, 'remainingAttempts', null),
    };
};
